import React from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { Card, message } from 'antd';
import { ProFormRadio, ProFormTextArea } from '@ant-design/pro-form';
import { useHistory, useParams } from 'react-router-dom';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { ProForm } from 'components/layout';
import { PrivateComponent } from 'components/auth';
import { SCOPE_TYPES } from 'constants/scopes';
import AssignEndoserModal from './AssignEndorserModal';
import {
  ENDORSE_PROJECT_APPLICATION,
  GET_PROJECT_APPLICATION,
} from 'gql/projectApplications';
import { getEndorserTitle } from '../utils';
import useIsCurrentEndorser from 'hooks/useIsCurrentEndoser';
import { GET_PENDING_APPROVALS } from 'gql/pendingApprovals';

const EndorsementForm = ({
  step,
  endorsementSequence,
  currentStepSequence,
}) => {
  const history = useHistory();
  const { id } = useParams();
  const { loading, isCurrentEndorser } = useIsCurrentEndorser(id);
  const [submit] = useMutation(ENDORSE_PROJECT_APPLICATION, {
    refetchQueries: [
      { query: GET_PROJECT_APPLICATION, variables: { id } },
      {
        query: GET_PENDING_APPROVALS,
        variables: {
          ignoreNotAuthorised: true,
        },
        errorPolicy: 'all',
      },
    ],
  });
  const { endorserListingType, appointedEndorser, stepSequence } = step;
  const disabled = stepSequence > currentStepSequence || !isCurrentEndorser;
  const onFinish = async (values) => {
    await submit({
      variables: {
        input: {
          ...values,
          endorsementSequence,
          stepSequence,
          id,
        },
      },
    });
    message.success('Endorsement saved successfully');
    if (!values.endorsed) {
      history.push('/project-applications/all');
      return;
    }
    return true;
  };
  return (
    <Card
      type="inner"
      title={`Endorsement by ${getEndorserTitle(
        appointedEndorser,
        endorserListingType
      )}`}
      loading={loading}
      extra={
        endorserListingType === 'ASSIGN' && (
          <PrivateComponent
            expectedScopeType={[
              SCOPE_TYPES.ASSIGN_PROJECT_APPLICATION_ENDORSER,
            ]}
          >
            <AssignEndoserModal
              initialValues={appointedEndorser?.user?.id}
              stepSequence={stepSequence}
              endorsementSequence={endorsementSequence}
              buttonText={
                appointedEndorser?.user ? (
                  <>
                    <EditOutlined /> Re-assign endorser
                  </>
                ) : (
                  <>
                    <PlusOutlined /> Assign endorser
                  </>
                )
              }
            />
          </PrivateComponent>
        )
      }
    >
      <ProForm
        onFinish={onFinish}
        disabled={disabled}
        previousLevel="/project-applications/all"
      >
        <ProFormRadio.Group
          disabled={disabled}
          name="endorsed"
          label="Decision / Approval"
          options={[
            {
              value: true,
              label: 'Endorse',
            },
            {
              value: false,
              label: 'Send back',
            },
          ]}
          rules={[{ required: true, message: 'Please select' }]}
        />
        <ProFormTextArea
          disabled={disabled}
          width="xxl"
          name="comment"
          label="Comment"
          rules={[{ required: true, message: 'Please enter comment' }]}
        />
      </ProForm>
    </Card>
  );
};

EndorsementForm.propTypes = {
  step: PropTypes.shape({
    endorserListingType: PropTypes.string,
    appointedEndorser: PropTypes.object,
  }).isRequired,
  endorsementSequence: PropTypes.number.isRequired,
  currentStepSequence: PropTypes.number,
};

export default EndorsementForm;
